import React, { useState } from 'react';
import { toast } from 'sonner';
import { X, Download, Share2, ExternalLink, FileText, Loader2 } from 'lucide-react';

export const PdfActionsModal = ({ open, onClose, url, filename, title, description }) => {
  const [loading, setLoading] = useState(null);

  if (!open) return null;

  const fetchBlob = async () => {
    const r = await fetch(url);
    if (!r.ok) throw new Error('Falha ao gerar o PDF');
    return r.blob();
  };

  const downloadBlob = (blob) => {
    const objUrl = window.URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = objUrl; a.download = filename;
    document.body.appendChild(a); a.click(); a.remove();
    window.URL.revokeObjectURL(objUrl);
  };

  const baixar = async () => {
    setLoading('baixar');
    try {
      downloadBlob(await fetchBlob());
      toast.success('PDF baixado');
    } catch (_e) {
      toast.error('Erro ao baixar o PDF');
    } finally {
      setLoading(null);
    }
  };

  const compartilhar = async () => {
    setLoading('compartilhar');
    try {
      const blob = await fetchBlob();
      const file = new File([blob], filename, { type: 'application/pdf' });
      if (navigator.canShare && navigator.canShare({ files: [file] })) {
        await navigator.share({ files: [file], title });
      } else {
        toast.info('Compartilhamento não suportado neste dispositivo. Baixando o PDF...');
        downloadBlob(blob);
      }
    } catch (e) {
      if (e && e.name !== 'AbortError') toast.error('Erro ao compartilhar o PDF');
    } finally {
      setLoading(null);
    }
  };

  const abrirNovaAba = () => {
    window.open(url, '_blank', 'noopener,noreferrer');
  };

  const Btn = ({ id, icon: Icon, label, onClick, primary }) => (
    <button
      onClick={onClick}
      disabled={!!loading}
      data-testid={`pdf-actions-${id}-btn`}
      className={`w-full flex items-center justify-center gap-2 px-4 py-3 rounded-lg font-medium transition-colors disabled:opacity-50 ${
        primary
          ? 'bg-yellow-500 text-black hover:bg-yellow-400'
          : 'bg-gray-800 text-gray-200 hover:bg-gray-700 border border-gray-700'
      }`}
    >
      {loading === id ? <Loader2 size={18} className="animate-spin" /> : <Icon size={18} />}
      <span>{label}</span>
    </button>
  );

  return (
    <div className="fixed inset-0 z-[100] flex items-center justify-center p-4" data-testid="pdf-actions-modal">
      <div className="absolute inset-0 bg-black/70" onClick={onClose} />
      <div className="relative w-full max-w-sm bg-[#1A1A1A] border border-gray-700 rounded-xl shadow-2xl p-6">
        <button
          onClick={onClose}
          data-testid="pdf-actions-fechar-x-btn"
          className="absolute top-3 right-3 p-2 rounded-lg text-gray-400 hover:text-white hover:bg-gray-800 transition-colors"
        >
          <X size={18} />
        </button>
        <div className="flex items-center gap-3 mb-2">
          <div className="p-2.5 rounded-lg bg-yellow-500/10 text-yellow-500">
            <FileText size={22} />
          </div>
          <h2 className="text-lg font-semibold text-white">{title}</h2>
        </div>
        {description && <p className="text-sm text-gray-400 mb-5">{description}</p>}
        <div className={`space-y-2.5 ${description ? '' : 'mt-4'}`}>
          <Btn id="baixar" icon={Download} label="Baixar PDF" onClick={baixar} primary />
          <Btn id="compartilhar" icon={Share2} label="Compartilhar" onClick={compartilhar} />
          <Btn id="nova-aba" icon={ExternalLink} label="Abrir em nova aba" onClick={abrirNovaAba} />
          <button
            onClick={onClose}
            data-testid="pdf-actions-fechar-btn"
            className="w-full px-4 py-2.5 rounded-lg text-gray-400 hover:text-white transition-colors text-sm"
          >
            Fechar
          </button>
        </div>
      </div>
    </div>
  );
};
